<template>
  <ModalForm
    :title="title"
    :disabled="isSaveAndExport || isExporting"
    class="modal--title-reset export-modal"
    hide-btn-submit
    btn-size="m"
    @reject="onReject"
  >
    <div class="f-wrap d-flex gap-s mb-20">
      <VCheckbox
        v-model="isAllChecked"
        label="Все поля партий"
        class="checkbox-all"
        @value-changed="selectAll"
      />
      <VButton
        text="Сбросить выбранное"
        icon="cross"
        block="link"
        class="btn--no-shadow-always text-underline"
        @click.native="resetColumns"
      />
    </div>
    <Spinner v-if="isLoadingColumns" class="mb-20" />
    <div v-else class="d-grid d-grid--columns-3 gap-12 mb-20 checkbox-fields">
      <VCheckbox
        v-for="col in columnsList"
        :key="col.key"
        v-model="selectedColumns[col.key]"
        :label="col.label"
      />
    </div>
    <template v-slot:customBtn>
      <VButton
        text="Сохранить и экспортировать"
        color="primary"
        size="l"
        style="order: -2"
        :loading="isSaveAndExport"
        :disabled="isExporting"
        @click.native="saveColumnsAndExport"
      />
      <VButton
        text="Экспортировать"
        color="primary"
        size="l"
        style="order: -1"
        :loading="isExporting"
        :disabled="isSaveAndExport"
        @click.native="getExportList"
      />
    </template>
  </ModalForm>
</template>

<script>
import { showAlertError, showAlertWarning } from '@/utils/store/alert'
import { getConsignmentStatusIdByRouteName } from '@/utils/order/consignment'
import router from '@/router'
import ConsignmentController from '@/controllers/production/Consignment/ConsignmentController'
import ModalForm from '@/components/ui/Modal/ModalForm.vue'
import VCheckbox from '@/components/Form/VCheckbox.vue'
import VButton from '@/components/simple/button/VButton.vue'
import { mapActions, mapGetters } from 'vuex'
import mediaService from '@/services/media.service'

export default {
  name: 'ModalConsignmentExportList',
  components: {
    VButton,
    VCheckbox,
    ModalForm
  },
  props: {
    title: {
      type: String,
      default: 'Экспорт списка партий в Excel'
    },
    tableColumnsCurrent: {
      type: [Object, Array],
      default: null
    }
  },
  data () {
    return {
      isAllChecked: false,
      isExporting: false,
      isSaveAndExport: false,
      isLoadingColumns: false,
      selectedColumns: {},
      defaultColumns: [
        'positions.orders.account_number',
        'positions.sort',
        'sort',
        'value',
        'positions.types.name',
        'positions.materials.name',
        'positions.d_wire',
        'positions.workshops.name',
        'statuses.name',
        'positions.orders.date_finished',
        'next_status'
      ]
    }
  },
  async created () {
    // Если колонки ещё не загружены — тянем с бэка
    if (!Object.keys(this.exportColumns).length) {
      this.isLoadingColumns = true
      try {
        await this.fetchExportColumns()
      } finally {
        this.isLoadingColumns = false
      }
    }
  },
  computed: {
    ...mapGetters({
      getQueryParams: 'consignment/getQueryParams',
      getSort: 'consignment/getSort',
      exportColumns: 'consignment/getExportColumns'
    }),

    // Проверка: выбрана хотя бы одна колонка
    hasSelectedColumns () {
      return Object.values(this.selectedColumns).some(Boolean)
    },

    // Преобразуем объект {key: label} → [{key, label}]
    columnsList () {
      return this.transformColumns(this.exportColumns)
    }
  },
  methods: {
    ...mapActions('consignment', {
      fetchExportColumns: 'getExportColumns'
    }),

    async saveColumnsAndExport () {
      // Флаг отдельного сценария (с сохранением meta)
      this.isSaveAndExport = true

      try {
        await this.getExportList()
      } finally {
        this.isSaveAndExport = false
      }
    },
    async getExportList () {
      if (!this.hasSelectedColumns) {
        showAlertWarning('Выберите хотя бы одно поле')
        return
      }
      // Определяем сценарий:
      // обычный экспорт или экспорт + сохранение meta
      const shouldSaveMeta = this.isSaveAndExport
      if (!shouldSaveMeta) this.isExporting = true

      try {
        showAlertWarning('Началась подготовка отчета, пожалуйста, подождите')

        const params = {
          status_line: getConsignmentStatusIdByRouteName(router.currentRoute.name),
          sort: this.getSort ?? '-id',
          meta: {
            export: {
              consignments: this.getSelectedColumns()
            }
          },
          save_meta: shouldSaveMeta,
          ...this.getQueryParams
        }

        const response = await ConsignmentController.getExport(params)
        if (response?.status !== 200) return

        // Если это сценарий "сохранить и экспортировать"
        if (shouldSaveMeta) this.updateUserMeta()

        await this.downloadFile(response)
        this.onReject()
      } catch (e) {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text()
          const json = JSON.parse(text) // { errors: { count: [...] } }
          showAlertError(json.errors)
        } else showAlertError(e)
      } finally {
        if (!shouldSaveMeta) this.isExporting = false
      }
    },

    async downloadFile (response) {
      const file = mediaService.createFileFromResponse(response)
      await mediaService.download(file)
    },

    // Закрываем модальное окно
    onReject () {
      this.$emit('reject')
    },

    // Массово включаем / выключаем все чекбоксы
    setAllColumns (value) {
      Object.keys(this.selectedColumns).forEach(key => {
        this.selectedColumns[key] = value
      })
    },

    // data.value приходит из VCheckbox
    selectAll (data) {
      this.setAllColumns(data.value)
    },

    // Сброс — выключаем всё
    resetColumns () {
      this.setAllColumns(false)
    },

    // Возвращаем только выбранные ключи
    getSelectedColumns () {
      return Object.keys(this.selectedColumns).filter(key => this.selectedColumns[key])
    },

    transformColumns (data) {
      if (!data) return []

      // {key: label} → [{key, label}]
      return Object.entries(data).map(([key, label]) => ({
        key,
        label
      }))
    },

    initSelectedColumns (cols) {
      if (!cols.length) return

      // Берём сохранённые колонки из meta
      const meta = this.$store.getters.userMeta
      const saved = meta?.export?.consignments || []
      const hasSaved = saved.length > 0

      const defaults = this.defaultColumns || []
      const hasDefaults = defaults.length > 0

      // Если есть сохранённые - используем их
      // иначе → включаем дефолтные или все
      this.selectedColumns = Object.fromEntries(
        cols.map(col => [
          col.key,
          hasSaved
            ? saved.includes(col.key)
            : hasDefaults
              ? defaults.includes(col.key)
              : true
        ])
      )
    },

    // Обновляем meta в сторе (локально)
    updateUserMeta () {
      const currentMeta = this.$store.getters.userMeta

      const newMeta = {
        ...(currentMeta || {}),
        export: {
          consignments: this.getSelectedColumns()
        }
      }

      this.$store.commit('SET_USER_META', newMeta)
    }
  },

  watch: {
    // Синхронизация чекбокса "Выбрать все"
    selectedColumns: {
      handler (val) {
        const values = Object.values(val)
        this.isAllChecked = values.length > 0 && values.every(Boolean)
      },
      deep: true,
      immediate: true
    },
    columnsList: {
      handler (cols) {
        if (!cols.length) return

        // не перезаписываем, если уже есть выбор
        if (Object.keys(this.selectedColumns).length) return

        this.initSelectedColumns(cols)
      },
      immediate: true
    }
  }
}
</script>
<style lang="scss" scoped>
.checkbox-fields {
  grid-auto-flow: column;
  grid-template-rows: repeat(13, auto);

  @media screen and (max-width: $tablet-width-s) {
    grid-auto-flow: row;
    grid-template-rows: auto;
  }
  &::v-deep .v-checkbox {
    width: max-content;
  }
}
.checkbox-all {
  background-color: #D6E3FF;
  padding: 2px 8px 2px 2px;
  border-radius: 4px;
}
.export-modal {
  &::v-deep .button-group {
    @media screen and (max-width: $mobile-width-l) {
      flex-direction: column;
      & > * {
        width: 100%;
      }
    }
  }
}
</style>
